import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import { scrollState } from "@/lib/scrollState";
import { Ambience } from "@/lib/ambient";
import { SCENES } from "@/content/story";
import JourneyCanvas from "@/components/JourneyCanvas";
import Navbar from "@/components/Navbar";
import ChapterRail from "@/components/ChapterRail";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Footer from "@/components/Footer";
import HUD from "@/components/HUD";
import ErrorBoundary from "@/components/ErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const updateProgress = () => {
    const limit = document.documentElement.scrollHeight - window.innerHeight;
    scrollState.progress = limit > 0 ? Math.min(1, Math.max(0, window.scrollY / limit)) : 0;
};

function Site() {
    const [act, setAct] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [voiceOn, setVoiceOn] = useState(false);
    const [ambientOn, setAmbientOn] = useState(false);
    const [exploring, setExploring] = useState(false);
    const sceneRef = useRef(null);
    const audioRef = useRef(null);
    const cacheRef = useRef(new Map());
    const ambienceRef = useRef(null);

    // smooth scroll + progress
    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let lenis = null;
        let raf = 0;

        if (!reduced) {
            lenis = new Lenis({ lerp: 0.09 });
            scrollState.lenis = lenis;
            const loop = (time) => {
                lenis.raf(time);
                updateProgress();
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
        } else {
            window.addEventListener("scroll", updateProgress, { passive: true });
            updateProgress();
        }

        return () => {
            if (raf) cancelAnimationFrame(raf);
            if (lenis) lenis.destroy();
            scrollState.lenis = null;
            window.removeEventListener("scroll", updateProgress);
        };
    }, []);

    // auto-play the journey
    useEffect(() => {
        if (!playing || exploring) return;
        let raf;
        let last = performance.now();
        const step = (now) => {
            const dt = Math.min((now - last) / 1000, 0.05);
            last = now;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const target = window.scrollY + (max / 85) * dt;
            if (scrollState.lenis) scrollState.lenis.scrollTo(target, { immediate: true });
            else window.scrollTo(0, target);
            if (target >= max - 2) {
                setPlaying(false);
                return;
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        const stop = () => setPlaying(false);
        window.addEventListener("wheel", stop, { passive: true });
        window.addEventListener("touchmove", stop, { passive: true });
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("wheel", stop);
            window.removeEventListener("touchmove", stop);
        };
    }, [playing, exploring]);

    // voice narration, synced to the active chapter
    useEffect(() => {
        const scene = SCENES[act];
        if (!voiceOn) {
            audioRef.current?.pause();
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            return;
        }
        let cancelled = false;
        const cached = cacheRef.current.get(scene.id);
        if (cached) {
            audioRef.current?.pause();
            const audio = new Audio(cached);
            audioRef.current = audio;
            audio.play().catch(() => {});
        } else {
            fetch(`${API}/narration/${scene.id}`)
                .then((r) => {
                    if (!r.ok) throw new Error("tts unavailable");
                    return r.blob();
                })
                .then((b) => {
                    if (cancelled) return;
                    const url = URL.createObjectURL(b);
                    cacheRef.current.set(scene.id, url);
                    audioRef.current?.pause();
                    const audio = new Audio(url);
                    audioRef.current = audio;
                    audio.play().catch(() => {});
                })
                .catch(() => {
                    if (cancelled) return;
                    try {
                        window.speechSynthesis.cancel();
                        const u = new SpeechSynthesisUtterance(scene.narration);
                        const voices = window.speechSynthesis.getVoices();
                        const indian = voices.find(
                            (v) =>
                                /en[-_]IN/i.test(v.lang) ||
                                /india|neerja|heera|kalpana|ravi/i.test(v.name)
                        );
                        if (indian) u.voice = indian;
                        u.rate = 0.92;
                        window.speechSynthesis.speak(u);
                    } catch (e) {}
                });
        }
        return () => {
            cancelled = true;
            audioRef.current?.pause();
            if (window.speechSynthesis) window.speechSynthesis.cancel();
        };
    }, [act, voiceOn]);

    // village ambience
    useEffect(() => {
        if (!ambienceRef.current) ambienceRef.current = new Ambience();
        if (ambientOn) ambienceRef.current.start();
        else ambienceRef.current.stop();
    }, [ambientOn]);

    return (
        <div className="relative font-sans text-ink">
            <JourneyCanvas
                onActChange={setAct}
                onUserNavigate={() => setPlaying(false)}
                onExploreChange={setExploring}
                onReady={(s) => (sceneRef.current = s)}
            />
            <div className="noise-overlay pointer-events-none fixed inset-0 z-[1]" />
            <Navbar />
            <ChapterRail />
            <main className="relative z-10">
                <Hero />
                <div className="pointer-events-none h-[500vh]" data-testid="journey-scroll-space" />
                <Marquee />
            </main>
            <Footer />

            {/* explore hint */}
            <AnimatePresence>
                {exploring && (
                    <motion.div
                        data-testid="explore-hint"
                        initial={{ opacity: 0, y: -14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="fixed inset-x-0 top-16 z-30 flex justify-center px-6"
                    >
                        <p className="rounded-full border border-line bg-white/80 px-5 py-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft backdrop-blur-md">
                            Exploring — click to grab the view · WASD / arrows to fly · Space up · C down · Shift boost · Esc to exit
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* minimal story caption, synced to the 3D chapters (hidden on the gallery — the panels speak for themselves) */}
            {act < SCENES.length - 1 && (
                <div className="pointer-events-none fixed inset-x-0 bottom-9 z-20 flex justify-center px-6">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={act}
                            data-testid="act-caption"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="rounded-2xl bg-cream/60 px-6 py-3 text-center backdrop-blur-md"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-terracotta">
                                {SCENES[act].act}
                            </p>
                            <p className="mt-1 font-display text-lg italic text-ink sm:text-xl">
                                {SCENES[act].caption}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>
            )}

            <HUD
                playing={playing}
                voiceOn={voiceOn}
                ambientOn={ambientOn}
                exploring={exploring}
                onPlay={() => setPlaying((p) => !p)}
                onVoice={() => setVoiceOn((v) => !v)}
                onAmbient={() => setAmbientOn((a) => !a)}
                onExplore={() => sceneRef.current?.toggleExplore()}
                onFly={(dir) => sceneRef.current?.setFly(dir)}
            />
        </div>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Site />} />
                </Routes>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
